// src/services/OfficeColourService.js
//
// Locations: the buildings Vero knows about, and their badge colours.
//
// These used to be read from each person's officeLocation in Entra, with only
// the colour kept here. In a real directory that field is largely blank, so
// Vero had no locations at all and doors had nothing to point at.
//
// A building is not an HR record. The list now lives in Vero and an admin adds
// to it directly. Grouping the display by country still comes from Entra's
// usageLocation, which is populated.
//
// The file, the table and the endpoints still say "office". Renaming them would
// touch the presence log, the door mappings, the display and the roster for no
// behavioural gain, and every one of those is a chance to break something that
// currently works.

import apiClient from './apiClient'

// Offered in the picker. Chosen to stay distinguishable on a wall screen from
// several metres away, which rules out pastels and near-neighbours.
export const PALETTE = [
  '#ed6e01', // Epoka orange
  '#1f3864', // Epoka navy
  '#0f9d58', // green
  '#7b3fa0', // purple
  '#b3261e', // red
  '#0288a6', // teal
  '#c2185b', // magenta
  '#5d4037'  // brown
]

export async function getOfficeColours() {
  const response = await apiClient.post('/GetOfficeColours', {})

  if (response.data?.status !== 'success') {
    throw new Error(response.data?.message ?? 'Could not read office colours')
  }

  // Keyed for lookup, since callers ask "what colour is this location" rather
  // than wanting the list.
  const map = {}
  for (const row of response.data.colours ?? []) {
    map[`${row.country_code}|${row.office_name}`] = row.colour
  }
  return map
}

/**
 * The locations themselves, as a list.
 *
 * Same call as getOfficeColours; a row existing IS the location existing, which
 * is why adding one is just setting a colour on a name that had none.
 */
export async function getLocations() {
  const response = await apiClient.post('/GetOfficeColours', {})

  if (response.data?.status !== 'success') {
    throw new Error(response.data?.message ?? 'Could not read locations')
  }

  return (response.data.colours ?? [])
    .map((row) => ({
      country: row.country_code,
      name: row.office_name,
      colour: row.colour
    }))
    .sort((a, b) => a.country.localeCompare(b.country) || a.name.localeCompare(b.name))
}

/** Adding a location is writing its first row. */
export async function addLocation(countryCode, name, colour) {
  return setOfficeColour(countryCode, name, colour)
}

export async function renameLocation(countryCode, oldName, newName) {
  const response = await apiClient.post('/RenameLocation', {
    country_code: countryCode,
    office_name: oldName,
    new_name: newName
  })

  if (response.data?.status !== 'success') {
    throw new Error(response.data?.message ?? 'Could not rename the location')
  }
}

export async function setOfficeColour(countryCode, officeName, colour) {
  const response = await apiClient.post('/SetOfficeColour', {
    country_code: countryCode,
    office_name: officeName,
    colour
  })

  if (response.data?.status !== 'success') {
    throw new Error(response.data?.message ?? 'Could not save the colour')
  }
}

export async function clearOfficeColour(countryCode, officeName) {
  const response = await apiClient.post('/ClearOfficeColour', {
    country_code: countryCode,
    office_name: officeName
  })

  if (response.data?.status !== 'success') {
    throw new Error(response.data?.message ?? 'Could not clear the colour')
  }
}

/**
 * The colour to use for an office, falling back to a neutral grey when none has
 * been chosen. A fallback rather than an automatic assignment: a colour nobody
 * picked would look deliberate and mislead.
 */
export const NO_COLOUR = '#8794a3'

export function colourFor(map, countryCode, officeName) {
  return map[`${countryCode}|${officeName}`] ?? NO_COLOUR
}
